/**
 * Scoring Algorithm for AI Visibility
 * 
 * Base: 5-platform weighted formula
 * - ChatGPT/AI Platforms: 40%
 * - Google Search: 30%
 * - Perplexity: 10% (locked)
 * - Claude: 10% (locked)
 * - Gemini: 10% (locked)
 * 
 * Free tier max: 70/100 (only 2 platforms scanned)
 * Paid tier max: 100/100 (all platforms)
 */

export interface ScoringInput {
  aiPlatformsFound: boolean;
  aiScore?: number;              // V2.1: Direct score from Discovery Mode (0, 20, 40)
  googleFound: boolean;
  googlePosition: number | null;
  googleConfidence?: 'high' | 'medium' | 'low'; // V2.1: Kept for backwards compatibility but not used
  isPaidUser?: boolean;
  // V2.1 Discovery Mode fields
  queryType?: 'discovery' | 'fallback' | 'not_found' | 'error';
  queryUsed?: string;
  rawResponseSnippet?: string;
}

export interface ScoringResult {
  currentScore: number;
  potentialScore: number;
  visibilityGap: number;
  platformsCovered: number;
  totalPlatforms: number;
  coveragePercent: number;
  statusMessage: string;
  scoreColor: 'emerald' | 'amber' | 'rose';
  aiPlatformsScore: number;
  googleAdjustedScore: number;
}

/**
 * V2.1 REWARD MODEL: Calculate Google score based on ranking position
 * No penalties - only rewards for good positions
 * 
 * Research: Top 20 domains capture 67% of all AI citations (Yang, AAAI 2025)
 * Higher positions yield exponentially more AI citation probability
 */
function calculateGoogleScore(position: number | null): number {
  // Not found or invalid position
  if (position === null || position === undefined || position <= 0) {
    return 0;
  }
  
  // Position 1-3: Excellent - AI citation zone
  // Research shows top 3 get majority of AI citations
  if (position <= 3) {
    return 30;
  }
  
  // Position 4-10: Good - still on page 1
  // Solid SEO foundation
  if (position <= 10) {
    return 20;
  }
  
  // Position 11-20: Present - on the radar
  // Has some visibility but needs improvement
  if (position <= 20) {
    return 10;
  }
  
  // Position 21+: Not effectively visible
  return 0;
}

/**
 * Get score color based on value (0-100 scale)
 * Updated for V2.1 single scale
 */
function getScoreColor(score: number, _isPaid: boolean): ScoringResult['scoreColor'] {
  if (score >= 50) return 'emerald';  // Strong foundation
  if (score >= 30) return 'amber';    // Partial visibility
  return 'rose';                       // Low visibility
}

/**
 * Get status message based on score (0-100 scale)
 * Updated for V2.1 messaging
 */
function getStatusMessage(score: number, _isPaid: boolean): string {
  if (score >= 50) return 'Strong foundation. Visible on scanned platforms';
  if (score >= 30) return 'Partial visibility. Room for improvement';
  return 'Low visibility. Significant optimization needed';
}

/**
 * Calculate comprehensive visibility score
 */
export function calculateVisibilityScore(input: ScoringInput): ScoringResult {
  const { aiScore, aiPlatformsFound, googleFound, googlePosition, isPaidUser = false } = input;
  
  // V2.1: AI Score comes directly from Discovery Mode (0, 20, or 40)
  // Use the explicit aiScore if provided, otherwise fall back to boolean conversion
  const aiPlatformsScore = aiScore !== undefined ? aiScore : (aiPlatformsFound ? 40 : 0);
  
  // V2.1: Google score uses Reward Model (no penalties)
  const googleAdjustedScore = googleFound ? calculateGoogleScore(googlePosition) : 0;
  
  // Locked platforms (not scanned in free tier) - 30% total weight
  const perplexityScore = 0;
  const claudeScore = 0;
  const geminiScore = 0;
  
  // V2.1: Simplified scoring - direct addition of component scores
  // AI score (0-40) + Google score (0-30) = 0-70 free tier max
  const rawScore = aiPlatformsScore + googleAdjustedScore + perplexityScore + claudeScore + geminiScore;
  
  const currentScore = Math.round(rawScore);
  
  // Calculate potential score (realistic estimate based on current data)
  // If found on AI platforms and Google, potential is higher
  const potentialMultiplier = aiPlatformsFound && googleFound ? 1.0 : 
                              aiPlatformsFound || googleFound ? 0.9 : 0.8;
  const potentialScore = Math.round(100 * potentialMultiplier);
  
  const visibilityGap = potentialScore - currentScore;
  
  // Platform coverage
  const platformsCovered = 2; // Currently we scan 2 platforms
  const totalPlatforms = 5;
  const coveragePercent = Math.round((platformsCovered / totalPlatforms) * 100);
  
  return {
    currentScore,
    potentialScore,
    visibilityGap,
    platformsCovered,
    totalPlatforms,
    coveragePercent,
    statusMessage: getStatusMessage(currentScore, isPaidUser),
    scoreColor: getScoreColor(currentScore, isPaidUser),
    aiPlatformsScore,
    googleAdjustedScore,
  };
}

/**
 * Generate missed opportunities based on scan results
 */
export interface MissedOpportunity {
  icon: 'AlertTriangle' | 'TrendingDown' | 'Users' | 'Lock';
  title: string;
  description: string;
  locked: boolean;
  ctaText: string;
}

export function getMissedOpportunities(
  googlePosition: number | null,
  googleFound: boolean
): MissedOpportunity[] {
  const opportunities: MissedOpportunity[] = [];
  
  // Always show for free users - missing platform coverage
  opportunities.push({
    icon: 'AlertTriangle',
    title: 'Only 2 AI platforms scanned',
    description: 'Additional AI platforms require extended analysis',
    locked: true,
    ctaText: 'Learn More'
  });
  
  // Show if Google position > 3 (not in top 3)
  if (googleFound && googlePosition !== null && googlePosition > 3) {
    opportunities.push({
      icon: 'TrendingDown',
      title: `Ranking at position #${googlePosition} (not in top 3)`,
      description: 'Higher positions yield exponentially more AI citations (Yang, AAAI 2025)',
      locked: false,
      ctaText: 'See optimization tips'
    });
  }
  
  // Show if not found on Google at all
  if (!googleFound) {
    opportunities.push({
      icon: 'TrendingDown',
      title: 'Not ranking for this keyword on Google',
      description: 'Organic search is crucial for AI visibility',
      locked: false,
      ctaText: 'See how to improve'
    });
  }
  
  // Always show competitor visibility as locked
  opportunities.push({
    icon: 'Users',
    title: 'Competitor visibility unknown',
    description: 'See which brands rank above you',
    locked: true,
    ctaText: 'Learn More'
  });
  
  return opportunities;
}
